import { useCallback, useMemo, useState, type ChangeEvent } from 'react'
import { Button, Input, Popconfirm, Select, Space, Table, Typography, type TableColumnsType } from 'antd'
import { DeleteOutlined, PlusOutlined, DownOutlined, RightOutlined } from '@ant-design/icons'
import { containerOf, fieldsOf, type FieldEdit, type FieldKind, type FieldPath, type StructuredField } from '../config/groups'
import type { StructuredValue } from '../config/structured'
import { VALUE_TYPES } from '../config/labels'
import { StructuredFields } from './StructuredFields'

type Props = { value: StructuredValue; path: FieldPath; disabled: boolean; depth?: 0 | 1; onAdvanced?: () => void;
  onEdit: (edit: FieldEdit) => void;
  onRemove: (field: StructuredField) => void; onAdd: (path: FieldPath, kind: FieldKind, name: string) => void }
const DELETE_ICON = <DeleteOutlined aria-hidden />
const ADD_ICON = <PlusOutlined aria-hidden />
const SCROLL = { x: 'max-content' as const }
const LOCALE = { emptyText: '暂无条目' }
type Row = { id: string; field: StructuredField; cells: StructuredField[] }

const Removal = ({ field, disabled, onRemove }: Pick<Props, 'disabled' | 'onRemove'> & { field: StructuredField }) => {
  const remove = useCallback(() => onRemove(field), [field, onRemove])
  return <Popconfirm title="删除此条目？" description="保存并应用后生效。" onConfirm={remove} okText="删除" cancelText="取消" disabled={disabled}>
    <Button type="text" icon={DELETE_ICON} aria-label={`删除${field.label}`} disabled={disabled} />
  </Popconfirm>
}

/** A table may expand once. Deeper values use the existing advanced source entry, never recursive navigation. */
export const GroupedTable = ({ value, path, disabled, depth = 0, onAdvanced, onEdit, onRemove, onAdd }: Props) => {
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const [kind, setKind] = useState<FieldKind>('string')
  const fields = useMemo(() => fieldsOf(value, path), [path, value])
  const arrayObjects = Array.isArray(value) && value.length > 0 && value.every((item) => item !== null && typeof item === 'object' && !Array.isArray(item))
  const rows = useMemo<Row[]>(() => fields.map((field) => ({ id: field.id, field,
    cells: arrayObjects ? fieldsOf(field.value, field.path) : [field] })), [arrayObjects, fields])
  const slots = useMemo(() => [...new Map(rows.flatMap((row) => row.cells.map((field) => [
    arrayObjects ? String(field.path.slice(-1)[0]) : 'value', field,
  ] as const))).entries()], [arrayObjects, rows])
  const canRemove = containerOf(value)
  const renderValue = useCallback((field: StructuredField) => <StructuredFields field={field} disabled={disabled} onEdit={onEdit} onAdvanced={depth === 1 || arrayObjects ? onAdvanced : undefined} />,
    [arrayObjects, depth, disabled, onAdvanced, onEdit])
  const columns = useMemo<TableColumnsType<Row>>(() => [
    { title: arrayObjects ? '条目' : '名称', key: 'label', width: 160, render: (_value, row) => row.field.label },
    ...slots.map(([key, field]) => ({ title: arrayObjects ? field.label : '内容', key,
      render: (_value: unknown, row: Row) => {
        const cell = row.cells.find((item) => !arrayObjects || String(item.path.slice(-1)[0]) === key)
        return cell ? renderValue(cell) : <Typography.Text type="secondary">未设置</Typography.Text>
      } })),
    ...(canRemove ? [{ title: '操作', key: 'actions', width: 64, render: (_value: unknown, row: Row) =>
      <Removal field={row.field} disabled={disabled} onRemove={onRemove} /> }] : []),
  ], [arrayObjects, canRemove, disabled, onRemove, renderValue, slots])
  const openAdd = useCallback(() => setAdding(true), [])
  const closeAdd = useCallback(() => { setAdding(false); setName('') }, [])
  const nameChanged = useCallback((event: ChangeEvent<HTMLInputElement>) => setName(event.target.value), [])
  const submit = useCallback(() => {
    try { onAdd(path, kind, name); closeAdd() } catch { /* The lifecycle owner displays the validation failure. */ }
  }, [closeAdd, kind, name, onAdd, path])
  const expand = useCallback((row: Row) => <GroupedTable value={row.field.value} path={row.field.path} disabled={disabled}
    depth={1} onAdvanced={onAdvanced} onEdit={onEdit} onRemove={onRemove} onAdd={onAdd} />,
  [disabled, onAdd, onAdvanced, onEdit, onRemove])
  const expandIcon = useCallback(({ expanded, onExpand, record }: {
    expanded: boolean; onExpand: (record: Row, event: React.MouseEvent<HTMLElement>) => void; record: Row;
  }) => !arrayObjects && containerOf(record.field.value) ? <ExpandRow expanded={expanded} record={record} onExpand={onExpand} /> : null, [arrayObjects])
  const expandable = useMemo(() => depth === 0 ? { expandedRowRender: expand,
    rowExpandable: (row: Row) => !arrayObjects && containerOf(row.field.value), expandIcon } : undefined, [arrayObjects, depth, expand, expandIcon])
  return <div className="instance-administration__group-editor">
    <Table<Row> columns={columns} dataSource={rows} rowKey="id" pagination={false} size="small" scroll={SCROLL} locale={LOCALE} expandable={expandable} />
    {canRemove ? <div className="instance-administration__group-add">
      {adding ? <Space wrap>
        {!Array.isArray(value) ? <Input aria-label="新增字段名称" placeholder="字段名称" value={name} onChange={nameChanged} disabled={disabled} /> : null}
        <Select aria-label="新增条目类型" value={kind} onChange={setKind} options={VALUE_TYPES} disabled={disabled} />
        <Button type="primary" onClick={submit} disabled={disabled || (!Array.isArray(value) && !name.trim())}>确定新增</Button>
        <Button onClick={closeAdd} disabled={disabled}>取消</Button>
      </Space> : <Button onClick={openAdd} icon={ADD_ICON} disabled={disabled}>新增条目</Button>}
    </div> : null}
  </div>
}


const ExpandRow = ({ expanded, record, onExpand }: {
  expanded: boolean; record: Row; onExpand: (record: Row, event: React.MouseEvent<HTMLElement>) => void;
}) => {
  const click = useCallback((event: React.MouseEvent<HTMLElement>) => onExpand(record, event), [onExpand, record])
  return <Button type="text" size="small" onClick={click} aria-label={(expanded ? '收起' : '展开') + record.field.label}
    aria-expanded={expanded} icon={expanded ? <DownOutlined /> : <RightOutlined />} />
}
